
import { useSidClient } from "@/plugins/auth";
import { useRouter } from "vue-router";
import { defineComponent, onMounted, ref } from "vue";
import { SID_ERROR_MSG_NOT_AUTHENTICATED, SID_ERROR_MSG_UNAUTHORIZED } from "@sid/sid_client";
import { Form, Field } from "vee-validate";
import { checkAuthPhoneNotReqd } from "@/commons/inputCheckCommon";
import * as Yup from "yup";
import useDeterring from "@/commons/useDeterring";
import { useStore } from "@/store";
import { UserBaseAuthPhone } from "@/commons/interfaces";
import { AuthPhoneItem } from "@/modules/authPhone";
import { convertAuthPhone, convertAuthPhoneNational, maskPhoneNumber } from "@/commons/commonUtility";
import {  getCountries, type CountryCode  } from "libphonenumber-js";

export default defineComponent({
    name: "AuthPhone",
    components: {
        Form,
        Field
    },
    setup() {
        const store = useStore();
        const sidClient = useSidClient();
        const router = useRouter();
        const { isDeterringRef, asyncDeterringWrap } = useDeterring();
        const authPhoneItem = AuthPhoneItem.inject();
        const userAuthPhone = authPhoneItem.authPhone as UserBaseAuthPhone;
        const currentAuthPhoneRef = ref("");   
        const newAuthPhoneRef = ref("");        
        const errorMsgRef = ref("");
        const phoneNumber = ref<string | null>(null);
        const isReady = ref(false);
        const schema = Yup.object().shape({
            newAuthPhone: checkAuthPhoneNotReqd
        }); 

        // 国リストの要素
        interface CountryInfo {
            code: CountryCode;
            name: string;
            flag: string;
        }
        const selectedCountry = ref<CountryCode>("JP"); // デフォルト国
        const submitType = ref("");

        // 国旗絵文字
        const getFlagEmoji = (countryCode: CountryCode) => {
            return String.fromCodePoint(...[...countryCode.toUpperCase()].map(char => 0x1F1E6 + char.charCodeAt(0) - "A".charCodeAt(0)));
        };
        // 国名
        const countryNameProvider = new Intl.DisplayNames(["ja"], { type: "region" });
        // 国コードリスト
        const countries: CountryInfo[] = getCountries().map((code: CountryCode): CountryInfo => ({
            code: code,
            name: countryNameProvider.of(code) || code, 
            flag: getFlagEmoji(code)
        }));


        /**
         * 入力内容を確認するボタン・削除ボタン押下時処理
         * @async
         */
        const authPhone = async () => {
            if (submitType.value === "confirm"){
                // 電話番号を解析
                phoneNumber.value = await convertAuthPhone(newAuthPhoneRef.value, "JP");
            }else{
                // 削除
                phoneNumber.value = null;
            }

            // 更新処理
            await sidClient.setAuthPhone(phoneNumber.value as string)
                .then(() => {
                    // 認証用電話番号変更確認画面へ遷移
                    router.push({ name: "AuthPhoneConfirm", query: { nowAuthPhone: currentAuthPhoneRef.value, newAuthPhone: newAuthPhoneRef.value,phoneNumber: phoneNumber.value,processMode: submitType.value } });
                })
                .catch(reason => {
                    if (reason.message === SID_ERROR_MSG_NOT_AUTHENTICATED || reason.message === SID_ERROR_MSG_UNAUTHORIZED) {
                    // ログイン画面へ遷移
                        router.push({ name: "Login" });
                    } else if (reason.response?.data === "invalid phone number format (E.164 required)") {
                        errorMsgRef.value = "認証用電話番号の形式が不正です。";    
                    } else if (reason.response?.data === "not registerable phone number") {
                        errorMsgRef.value = "この認証用電話番号への変更はできません。";
                    } else if (reason.response?.data === "phone number is not changed") {
                        errorMsgRef.value = "認証用電話番号が登録前と同じ認証用電話番号の為、変更されておりません。";                        
                    } else {
                        errorMsgRef.value = "認証用電話番号変更に失敗しました。後ほど再度お試し下さい。";
                    }
                });
        };

        onMounted(async () => {
            try {
                // 基本属性情報再取得して保持
                await store.dispatch("fetchUserAuthPhone");
                authPhoneItem.setAuthPhone(store.state.userAuthPhone);
                isReady.value = true;
            } catch (e) {
                // 特に何もしない
            }

            currentAuthPhoneRef.value = convertAuthPhoneNational(store.state.userAuthPhone.phoneNumber, "JP");
            
        });

        return {    
            isDeterringRef,
            asyncDeterringWrap,
            userAuthPhone,                    
            currentAuthPhoneRef,
            newAuthPhoneRef,
            phoneNumber,
            errorMsgRef,
            schema,
            authPhone,
            router,
            submitType,
            countries,
            selectedCountry,
            isReady,
            maskPhoneNumber,
        };
    },
});
